import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
} from '@mui/material';
import { Add, Save, Edit, Settings } from '@mui/icons-material';
import ModernLayout from './common/ModernLayout';
import ModernCard from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import WidgetFactory from './dashboard-widgets/WidgetFactory';
import WidgetSelector from './dashboard-widgets/WidgetSelector';
import axiosInstance from './axiosConfig';
import { TemperatureUnitProvider } from './TemperatureUnitContext';
import { useLanguage } from '../contexts/LanguageContext';

const ModularDashboard = () => {
  const { translate } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [openWidgetSelector, setOpenWidgetSelector] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [isEditMode, setIsEditMode] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [selectedWidgetForSettings, setSelectedWidgetForSettings] = useState(null);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.log('No userId found, redirecting to login');
      window.location.href = '/';
    } else {
      console.log('Loading dashboard for userId:', userId);
      fetchProjects(userId);
      loadUserWidgets();
    }
  }, []);

  useEffect(() => {
    if (selectedProject) {
      console.log('Selected project:', selectedProject);
      localStorage.setItem('projectId', selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async (userId) => {
    try {
      console.log('Fetching projects for userId:', userId);
      const response = await axiosInstance.get(`/api/projects/${userId}`);
      console.log('Projects response:', response.data);
      setProjects(response.data);
      if (response.data.length > 0) {
        const savedProjectId = localStorage.getItem('projectId');
        if (savedProjectId && response.data.find(p => p.id === savedProjectId)) {
          setSelectedProject(savedProjectId);
        } else {
          setSelectedProject(response.data[0].id);
        }
      } else {
        console.log('No projects found for user');
      }
    } catch (error) {
      console.error('Error fetching projects', error);
      // Set a default project if API fails
      setSelectedProject('default');
    }
  };

  const loadUserWidgets = () => {
    const savedWidgets = localStorage.getItem('userDashboardWidgets');
    if (savedWidgets) {
      try {
        const parsedWidgets = JSON.parse(savedWidgets);
        setWidgets(parsedWidgets);
      } catch (error) {
        console.error('Error parsing saved widgets:', error);
        // Set default widgets if parsing fails
        setDefaultWidgets();
      }
    } else {
      setDefaultWidgets();
    }
  };

  const setDefaultWidgets = () => {
    const defaultWidgets = [
      {
        id: 'widget-1',
        type: 'room-sensors',
        title: translate('roomSensors'),
        position: { x: 0, y: 0, w: 12, h: 6 }
      },
      {
        id: 'widget-2',
        type: 'employees',
        title: translate('employees'),
        position: { x: 0, y: 6, w: 6, h: 6 }
      },
      {
        id: 'widget-3',
        type: 'access-logs',
        title: translate('accessLogs'),
        position: { x: 6, y: 6, w: 6, h: 6 }
      }
    ];
    setWidgets(defaultWidgets);
    saveUserWidgets(defaultWidgets);
  };

  const saveUserWidgets = (widgetsToSave) => {
    localStorage.setItem('userDashboardWidgets', JSON.stringify(widgetsToSave));
  };

  const handleAddWidget = (widgetConfig) => {
    const newWidget = {
      id: `widget-${Date.now()}`,
      type: widgetConfig.id,
      title: widgetConfig.title,
      position: { x: 0, y: 0, w: 6, h: 4 }
    };
    
    const updatedWidgets = [...widgets, newWidget];
    setWidgets(updatedWidgets);
    saveUserWidgets(updatedWidgets);
    showMessage(translate('widgetAddedSuccessfully'), 'success');
  };

  const handleRemoveWidget = (widgetId) => {
    const updatedWidgets = widgets.filter(widget => widget.id !== widgetId);
    setWidgets(updatedWidgets);
    saveUserWidgets(updatedWidgets);
    showMessage(translate('widgetRemovedSuccessfully'), 'success');
  };

  const handleWidgetSettings = (widget) => {
    setSelectedWidgetForSettings(widget);
    setOpenSettingsDialog(true);
  };

  const handleSaveWidgetSettings = (settings) => {
    const updatedWidgets = widgets.map(widget => 
      widget.id === selectedWidgetForSettings.id 
        ? { ...widget, ...settings }
        : widget
    );
    setWidgets(updatedWidgets);
    saveUserWidgets(updatedWidgets);
    setOpenSettingsDialog(false);
    setSelectedWidgetForSettings(null);
    showMessage(translate('widgetSettingsSaved'), 'success');
  };

  const showMessage = (msg, severity) => {
    setMessage(msg);
    setAlertSeverity(severity);
    setOpenSnackbar(true);
  };

  const getWidgetSize = (widgetType) => {
    switch (widgetType) {
      case 'room-sensors':
        return { w: 12, h: 8 };
      case 'employees':
        return { w: 6, h: 6 };
      case 'access-logs':
        return { w: 6, h: 6 };
      default:
        return { w: 6, h: 4 };
    }
  };

  return (
    <ModernLayout title={translate('modularDashboard')}>
      <TemperatureUnitProvider projectId={selectedProject}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            {translate('modularDashboard')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {translate('customizeDashboardDescription')}
          </Typography>
        </Box>

        {/* Project Selection */}
        <ModernCard
          title={translate('selectProject')}
          subtitle={translate('chooseProjectToViewData')}
          sx={{ mb: 3 }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {projects.map((project) => (
              <Chip
                key={project.id}
                label={project.name}
                variant={selectedProject === project.id ? 'filled' : 'outlined'}
                color={selectedProject === project.id ? 'primary' : 'default'}
                onClick={() => setSelectedProject(project.id)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              />
            ))}
          </Box>
        </ModernCard>

        {/* Dashboard Controls */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'center' }, 
          gap: { xs: 2, md: 0 },
          mb: 3 
        }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {translate('myDashboard')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('widgetsConfigured', { count: widgets.length })}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            width: { xs: '100%', md: 'auto' }
          }}>
            <GlassButton
              variant={isEditMode ? 'contained' : 'outlined'}
              startIcon={<Edit />}
              onClick={() => setIsEditMode(!isEditMode)}
              fullWidth={false}
            >
              {isEditMode ? translate('exitEditMode') : translate('editDashboard')}
            </GlassButton>
            <GradientButton
              startIcon={<Add />}
              onClick={() => setOpenWidgetSelector(true)}
              fullWidth={false}
            >
              {translate('addWidget')}
            </GradientButton>
          </Box>
        </Box>

        {/* Widgets Grid */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {widgets && widgets.length > 0 ? (
            widgets.map((widget) => (
              <Grid item xs={12} sm={6} md={widget.position.w >= 6 ? 12 : 6} key={widget.id}>
                <ModernCard
                  title={widget.title}
                  action={
                    isEditMode && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1 
                      }}>
                        <ModernButton
                          size="small"
                          startIcon={<Settings />}
                          onClick={() => handleWidgetSettings(widget)}
                          variant="text"
                          fullWidth={false}
                        >
                          {translate('configure')}
                        </ModernButton>
                        <ModernButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveWidget(widget.id)}
                          variant="text"
                          fullWidth={false}
                        >
                          {translate('delete')}
                        </ModernButton>
                      </Box>
                    )
                  }
                  sx={{
                    height: { xs: 'auto', md: `${widget.position.h * 100}px` },
                    minHeight: { xs: '250px', md: '300px' },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                      height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '3px',
                      '&:hover': {
                        background: 'rgba(0, 0, 0, 0.5)',
                      },
                    },
                  }}>
                    <WidgetFactory
                      widget={widget}
                      projectId={selectedProject}
                      onSettings={() => handleWidgetSettings(widget)}
                      onRemove={() => handleRemoveWidget(widget.id)}
                    />
                  </Box>
                </ModernCard>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <ModernCard
                title={translate('noWidgetsConfigured')}
                subtitle={translate('addWidgetsToCustomizeDashboard')}
                sx={{ textAlign: 'center', py: 4 }}
              >
                <GradientButton
                  startIcon={<Add />}
                  onClick={() => setOpenWidgetSelector(true)}
                  sx={{ mt: 2 }}
                >
                  {translate('addFirstWidget')}
                </GradientButton>
              </ModernCard>
            </Grid>
          )}
        </Grid>

        {/* Widget Selector Dialog */}
        <WidgetSelector
          open={openWidgetSelector}
          onClose={() => setOpenWidgetSelector(false)}
          onAddWidget={handleAddWidget}
          existingWidgets={widgets}
        />

        {/* Widget Settings Dialog */}
        <Dialog
          open={openSettingsDialog}
          onClose={() => setOpenSettingsDialog(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={false}
          PaperProps={{
            sx: {
              background: 'white',
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              m: { xs: 2, md: 0 },
              maxHeight: { xs: '90vh', md: '80vh' },
            },
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
            fontSize: '1.3rem',
          }}>
            {translate('configureWidget')}: {selectedWidgetForSettings?.title}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label={translate('widgetTitle')}
                defaultValue={selectedWidgetForSettings?.title}
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'rgba(0, 0, 0, 0.87)',
                  },
                }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                  {translate('size')}
                </InputLabel>
                <Select
                  defaultValue="medium"
                  label={translate('size')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiSelect-select': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                >
                  <MenuItem value="small">{translate('small')}</MenuItem>
                  <MenuItem value="medium">{translate('medium')}</MenuItem>
                  <MenuItem value="large">{translate('large')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, md: 3 }, 
            pt: 0,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Button 
              onClick={() => setOpenSettingsDialog(false)}
              sx={{ 
                textTransform: 'none',
                color: 'rgba(0, 0, 0, 0.7)',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {translate('cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => handleSaveWidgetSettings({ title: 'Updated Title' })}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
              }}
            >
              {translate('save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={alertSeverity}
            sx={{
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {message}
          </Alert>
        </Snackbar>
      </TemperatureUnitProvider>
    </ModernLayout>
  );
};

export default ModularDashboard; 